import { NavLink as RouterNavLink, NavLinkProps, useNavigate, useLocation } from "react-router-dom";
import { forwardRef, useTransition, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { useNavigation } from "@/contexts/NavigationContext";

interface NavLinkCompatProps extends Omit<NavLinkProps, "className"> {
  className?: string;
  activeClassName?: string;
  pendingClassName?: string;
}

const NavLink = forwardRef<HTMLAnchorElement, NavLinkCompatProps>(
  ({ className, activeClassName, pendingClassName, to, onClick, ...props }, ref) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isPending, startTransition] = useTransition();
    const { endNavigation } = useNavigation();
    const prevPathRef = useRef(location.pathname);
    
    // End navigation when route actually changes
    useEffect(() => {
      if (prevPathRef.current !== location.pathname) {
        endNavigation();
        prevPathRef.current = location.pathname;
      }
    }, [location.pathname, endNavigation]);
    
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      // Let default behavior happen for Ctrl/Cmd+Click or middle mouse button
      if (e.ctrlKey || e.metaKey || e.button === 1) {
        return;
      }

      // For normal clicks, use startTransition for non-blocking navigation
      e.preventDefault();
      startTransition(() => {
        navigate(to as string);
      });
      
      onClick?.(e);
    };

    return (
      <RouterNavLink
        ref={ref}
        to={to}
        onClick={handleClick}
        className={({ isActive }) =>
          cn(
            className, 
            isActive && activeClassName, 
            isPending && pendingClassName
          )
        }
        {...props}
      />
    );
  },
);

NavLink.displayName = "NavLink";

export { NavLink };
